import { fileIdOf } from './samples'
import { candidateInfo, isAwaitingAnalysis } from './candidates'
import { readJson, writeJson } from './storage'

// ----------------------------------------------------------------------
// Home's "Ready to analyse" strip: recent uploads from the panel's "Uploaded, not
// analysed yet" list, minus the ones the user hid with "Not for analysis".
// Hidden files are kept per user in this browser as { [fileId]: hiddenAtMs }.
// ----------------------------------------------------------------------

export const READY_WINDOW_DAYS = 30
const DAY_MS = 24 * 60 * 60 * 1000
export const READY_WINDOW_MS = READY_WINDOW_DAYS * DAY_MS

// A file hidden this long ago was uploaded before the window, so it can go; twice
// the window leaves room for a clock that is off.
const HIDDEN_KEEP_MS = 2 * READY_WINDOW_MS
const HIDDEN_MAX = 500

export const hiddenKey = (userKey) => `genesus.home.readyToAnalyse.hidden.${userKey || 'anonymous'}`

const cleanHidden = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  const out = {}
  Object.entries(value).forEach(([id, at]) => {
    if (id && typeof at === 'number' && Number.isFinite(at) && at > 0) out[id] = at
  })
  return out
}

/** Drops entries older than the window allows and keeps at most HIDDEN_MAX, newest first. */
export const pruneHidden = (hidden, now = Date.now()) => {
  const entries = Object.entries(cleanHidden(hidden))
    .filter(([, at]) => now - at <= HIDDEN_KEEP_MS)
    .sort((a, b) => b[1] - a[1])
    .slice(0, HIDDEN_MAX)
  return Object.fromEntries(entries)
}

export const loadHidden = (userKey, now = Date.now()) => pruneHidden(readJson(hiddenKey(userKey)), now)

export const saveHidden = (userKey, hidden, now = Date.now()) => writeJson(hiddenKey(userKey), pruneHidden(hidden, now))

export const hideFiles = (hidden, fileIds, now = Date.now()) => {
  const next = { ...hidden }
  fileIds.forEach((id) => {
    if (id !== null && id !== undefined && id !== '') next[String(id)] = now
  })
  return next
}

export const unhideFiles = (hidden, fileIds) => {
  const next = { ...hidden }
  fileIds.forEach((id) => {
    delete next[String(id)]
  })
  return next
}

// Hidden while every file of the sample is hidden: a lane uploaded later brings
// the sample back, since it is new data.
export const isHiddenCandidate = (candidate, hidden) => {
  const rows = candidate?.rows || []
  return rows.length > 0 && rows.every((row) => Boolean(hidden[String(fileIdOf(row))]))
}

/**
 * The samples for the strip, newest upload first:
 * [{ id, key, candidate, fileIds, verifying, uploadedAt, typeLabel }].
 * Same rule as the panel's list (isAwaitingAnalysis), uploaded within the last
 * READY_WINDOW_DAYS days and not hidden.
 */
export const readyToAnalyse = (candidates = [], analysed = new Set(), { hidden = {}, now = Date.now() } = {}) =>
  candidates
    .filter((candidate) => isAwaitingAnalysis(candidate, analysed, { now }) && !isHiddenCandidate(candidate, hidden))
    .map((candidate) => ({ candidate, info: candidateInfo(candidate, { now }) }))
    .filter(({ info }) => info.uploadedAt > 0 && now - info.uploadedAt <= READY_WINDOW_MS)
    .sort((a, b) => b.info.uploadedAt - a.info.uploadedAt)
    .map(({ candidate, info }) => ({
      id: candidate.id,
      key: candidate.key,
      candidate,
      fileIds: candidate.rows.map(fileIdOf),
      verifying: info.state === 'verifying',
      uploadedAt: info.uploadedAt,
      typeLabel: info.typeLabel,
    }))

/**
 * How many chips fit on one line, given each chip's width, the line's width, the
 * gap between chips and the width of the "+N more" chip shown after them. `total`
 * is the number of samples when only the first few were measured, so "+N more"
 * keeps its room after the last measured chip. At least one chip is shown; it
 * ellipsises when even it does not fit. Unmeasured (no width) shows them all.
 */
export const fitChips = (widths, available, gap, moreWidth, total = widths.length) => {
  if (!(available > 0)) return widths.length
  const count = Math.max(total, widths.length)
  let used = 0
  for (let i = 0; i < widths.length; i += 1) {
    const width = widths[i] + (i ? gap : 0)
    const reserve = i < count - 1 ? gap + moreWidth : 0
    if (used + width + reserve > available) return Math.max(1, i)
    used += width
  }
  return widths.length
}
